import React, { useState, useRef, useEffect, useMemo } from 'react';
import Icon from '../../../../components/AppIcon';
import { useUserPreferences } from '../../../../contexts/UserPreferencesContext';
import { Pagination } from '../../../../components/ui/Pagination';

// Format date - supports user preference
const formatDate = (dateString, dateFormat = 'DD/MM/YYYY') => {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const monthShort = d.toLocaleDateString('en-AU', { month: 'short' });
    const year = d.getFullYear();

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${monthShort} ${day}, ${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD/MM/YYYY':
      default:
        return `${day}/${month}/${year}`;
    }
  } catch {
    return dateString;
  }
};

const WorkforceTable = ({
  members,
  onStatusChange,
  onRoleChange,
  loading,
  type,
  canManage = false,
  canManagePermissions = false,
  onManagePermissions
}) => {
  const { preferences } = useUserPreferences();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRefs = useRef({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle dropdown toggle with position calculation
  const handleDropdownToggle = (memberId) => {
    if (openDropdown === memberId) {
      setOpenDropdown(null);
    } else {
      const button = buttonRefs.current[memberId];
      if (button) {
        const rect = button.getBoundingClientRect();
        const dropdownHeight = canManagePermissions ? 144 : 96; // Approximate height based on items
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // If not enough space below and more space above, show above
        const showAbove = spaceBelow < dropdownHeight + 16 && spaceAbove > spaceBelow;

        setDropdownPosition({
          top: showAbove ? rect.top - dropdownHeight - 8 : rect.bottom + 8,
          left: rect.right - 192 // 192px = w-48
        });
      }
      setOpenDropdown(memberId);
    }
  };

  // Filter members based on search and role
  const filteredMembers = useMemo(() => {
    return members?.filter(member => {
      const matchesSearch =
        member?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesRole = filterRole === 'all' || member?.role === filterRole;
      return matchesSearch && matchesRole;
    }) || [];
  }, [members, searchTerm, filterRole]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole]);

  // Calculate paginated members
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredMembers.slice(startIndex, startIndex + pageSize);
  }, [filteredMembers, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredMembers.length / pageSize);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'primary_owner': 'bg-purple-500 bg-opacity-20 text-purple-400',
      'owner': 'bg-purple-500 bg-opacity-20 text-purple-400',
      'executive': 'bg-blue-500 bg-opacity-20 text-blue-400',
      'general_manager': 'bg-blue-500 bg-opacity-20 text-blue-400',
      'department_head': 'bg-green-500 bg-opacity-20 text-green-400',
      'manager': 'bg-green-500 bg-opacity-20 text-green-400',
      'supervisor': 'bg-yellow-500 bg-opacity-20 text-yellow-400',
      'staff': 'bg-gray-500 bg-opacity-20 text-gray-400',
      'contractor': 'bg-orange-500 bg-opacity-20 text-orange-400'
    };
    return colors?.[role] || 'bg-gray-500 bg-opacity-20 text-gray-400';
  };

  const formatRole = (role) => {
    return role?.split('_')?.map(word =>
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ') || 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Icon
            name="Search"
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
          />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-primary"
          />
        </div>

        {type === 'employees' && (
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e?.target?.value)}
            className="px-4 py-2 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
          >
            <option value="all">All Roles</option>
            <option value="primary_owner">Primary Owner</option>
            <option value="owner">Owner</option>
            <option value="executive">Executive</option>
            <option value="general_manager">General Manager</option>
            <option value="department_head">Department Head</option>
            <option value="manager">Manager</option>
            <option value="supervisor">Supervisor</option>
            <option value="staff">Staff</option>
          </select>
        )}
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-text-secondary font-medium">Name</th>
              <th className="text-left py-3 px-4 text-text-secondary font-medium">Email</th>
              <th className="text-left py-3 px-4 text-text-secondary font-medium">Role</th>
              <th className="text-left py-3 px-4 text-text-secondary font-medium">Status</th>
              <th className="text-left py-3 px-4 text-text-secondary font-medium">Joined</th>
              {canManage && <th className="text-right py-3 px-4 text-text-secondary font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedMembers?.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 6 : 5} className="text-center py-12">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-background rounded-full flex items-center justify-center mb-4">
                      <Icon name="Users" size={32} className="text-text-secondary" />
                    </div>
                    <p className="text-text-secondary">No {type} found</p>
                    <p className="text-text-secondary text-sm mt-1">
                      {searchTerm ? 'Try adjusting your search' : `Add ${type} to get started`}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedMembers?.map((member) => (
                <tr key={member?.id} className="border-b border-border hover:bg-surface transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      {member?.avatarUrl ? (
                        <img
                          src={member.avatarUrl}
                          alt={member?.fullName || 'User'}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {member?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="text-text-primary font-medium">{member?.fullName || 'Unknown'}</p>
                        <p className="text-text-secondary text-sm">{member?.companyAlias}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-text-secondary">{member?.email}</td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member?.role)}`}>
                      {formatRole(member?.role)}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    {canManage ? (
                      <button
                        onClick={() => onStatusChange?.(member?.id, !member?.isActive)}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${member?.isActive
                          ? 'bg-green-500 bg-opacity-20 text-green-400 hover:bg-opacity-30' : 'bg-red-500 bg-opacity-20 text-red-400 hover:bg-opacity-30'
                          }`}
                      >
                        <Icon name={member?.isActive ? 'CheckCircle' : 'XCircle'} size={14} />
                        <span>{member?.isActive ? 'Active' : 'Inactive'}</span>
                      </button>
                    ) : (
                      <span className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${member?.isActive
                        ? 'bg-green-500 bg-opacity-20 text-green-400' : 'bg-red-500 bg-opacity-20 text-red-400'
                        }`}
                      >
                        <Icon name={member?.isActive ? 'CheckCircle' : 'XCircle'} size={14} />
                        <span>{member?.isActive ? 'Active' : 'Inactive'}</span>
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-text-secondary">
                    {formatDate(member.createdAt, preferences.date_format)}
                  </td>
                  {canManage && (
                    <td className="py-4 px-4 text-right">
                      <button
                        ref={el => buttonRefs.current[member?.id] = el}
                        onClick={() => handleDropdownToggle(member?.id)}
                        className="text-text-secondary hover:text-text-primary transition-colors p-1 rounded hover:bg-surface-light"
                      >
                        <Icon name="MoreVertical" size={20} />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredMembers.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredMembers.length}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showPageSizeSelector={true}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      )}

      {/* Fixed Position Dropdown Menu */}
      {openDropdown && (
        <div
          ref={dropdownRef}
          className="fixed w-48 bg-surface border border-border rounded-lg shadow-xl z-50"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          {canManagePermissions && (
            <button
              onClick={() => {
                const member = members?.find(m => m?.id === openDropdown);
                onManagePermissions?.(member);
                setOpenDropdown(null);
              }}
              className="w-full flex items-center space-x-2 px-4 py-3 text-text-secondary hover:bg-surface-light hover:text-text-primary transition-colors text-left rounded-t-lg"
            >
              <Icon name="Shield" size={16} />
              <span>Manage Permissions</span>
            </button>
          )}
          <button
            onClick={() => {
              const member = members?.find(m => m?.id === openDropdown);
              onRoleChange?.(member);
              setOpenDropdown(null);
            }}
            className="w-full flex items-center space-x-2 px-4 py-3 text-text-secondary hover:bg-surface-light hover:text-text-primary transition-colors text-left"
          >
            <Icon name="UserCog" size={16} />
            <span>Change Role</span>
          </button>
          <button
            onClick={() => {
              const member = members?.find(m => m?.id === openDropdown);
              onStatusChange?.(member?.id, !member?.isActive);
              setOpenDropdown(null);
            }}
            className="w-full flex items-center space-x-2 px-4 py-3 text-text-secondary hover:bg-surface-light hover:text-text-primary transition-colors text-left rounded-b-lg"
          >
            {(() => {
              const member = members?.find(m => m?.id === openDropdown);
              return (
                <>
                  <Icon name={member?.isActive ? 'UserMinus' : 'UserCheck'} size={16} />
                  <span>{member?.isActive ? 'Deactivate' : 'Activate'}</span>
                </>
              );
            })()}
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkforceTable;